import {
  Component,
  OnInit,
  OnDestroy,
  NgZone,
  ElementRef,
  ViewChild,
  AfterViewInit
} from '@angular/core';
import { HttpClient }   from '@angular/common/http';
import { Router }       from '@angular/router';
import { environment }  from 'src/app/environments/environment';
import Swal             from 'sweetalert2';

declare const google: any;

interface Worker {
  id:       string;
  fullName: string;
  email:    string;
  role:     string;
}

@Component({
  selector:    'app-create-job',
  templateUrl: './create-job.component.html',
  styleUrls:   ['./create-job.component.css']
})
export class CreateJobComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('locationInput') locationInputRef!: ElementRef<HTMLInputElement>;

  private api = environment.apiUrl;

  // ── Form fields ───────────────────────────────────────
  title        = '';
  description  = '';
  locationName = '';
  assignedDate = '';
  assignedUserId: string | null = null;

  // ── Location coordinates captured from Google Places ─
  latitude:  number | null = null;
  longitude: number | null = null;

  // ── State ─────────────────────────────────────────────
  workers:     Worker[] = [];
  loading      = false;
  loadingWorkers = true;
  error:       string | null = null;
  success      = false;
  locationConfirmed = false;

  // ── Google Places autocomplete instance ───────────────
  private autocomplete: any = null;
  private mapsLoaded        = false;

  constructor(
    private http:   HttpClient,
    private router: Router,
    private zone:   NgZone
  ) {}

  ngOnInit(): void {
    this.loadWorkers();
    this.loadGoogleMapsScript();
  }

  ngAfterViewInit(): void {
    // If Maps already loaded before view was ready, init now.
    if (this.mapsLoaded) this.initAutocomplete();
  }

  ngOnDestroy(): void {
    // Clean up autocomplete listeners.
    if (this.autocomplete && google?.maps?.event)
      google.maps.event.clearInstanceListeners(this.autocomplete);
  }


  // ─────────────────────────────────────────────────────
  // GOOGLE MAPS SCRIPT LOADING
  // ─────────────────────────────────────────────────────

  private loadGoogleMapsScript(): void {
    // If already loaded, just init the autocomplete.
    if (typeof google !== 'undefined' && google?.maps?.places) {
      this.mapsLoaded = true;
      this.initAutocomplete();
      return;
    }

    // Check if script tag already exists.
    const existing = document.getElementById('google-maps-script');
    if (existing) {
      existing.addEventListener('load', () => {
        this.mapsLoaded = true;
        this.initAutocomplete();
      });
      return;
    }

    const script    = document.createElement('script');
    script.id       = 'google-maps-script';
    script.src      = `https://maps.googleapis.com/maps/api/js?key=AIzaSyDhCNlbU5LYErY6mDmqctoATDRPxB5FhiM&libraries=places`;
    script.async    = true;
    script.defer    = true;

    script.onload = () => {
      this.mapsLoaded = true;
      this.initAutocomplete();
    };

    script.onerror = () => {
      console.error('[CreateJob] Failed to load Google Maps script.');
    };

    document.head.appendChild(script);
  }

  private initAutocomplete(): void {
    if (!this.locationInputRef?.nativeElement) return;

    try
    {
      this.autocomplete = new google.maps.places.Autocomplete(
        this.locationInputRef.nativeElement,
        {
          types:  ['geocode', 'establishment'],
          fields: ['name', 'formatted_address', 'geometry']
        }
      );

      this.autocomplete.addListener('place_changed', () => {
        this.zone.run(() => {
          const place = this.autocomplete.getPlace();

          if (!place?.geometry?.location) {
            // User typed something but did not select a suggestion.
            this.latitude          = null;
            this.longitude         = null;
            this.locationConfirmed = false;
            return;
          }

          // Capture coordinates from the selected place.
          this.latitude  = place.geometry.location.lat();
          this.longitude = place.geometry.location.lng();

          // Use formatted address as the display name, falling back to name.
          this.locationName      = place.formatted_address
                                ?? place.name
                                ?? this.locationName;
          this.locationConfirmed = true;

          console.log(
            `[CreateJob] Location selected: ${this.locationName} ` +
            `(${this.latitude}, ${this.longitude})`
          );
        });
      });

      console.log('[CreateJob] Google Places autocomplete initialised.');
    }
    catch (err)
    {
      console.error('[CreateJob] Failed to init autocomplete:', err);
    }
  }


  // ─────────────────────────────────────────────────────
  // LOAD WORKERS
  // ─────────────────────────────────────────────────────

  loadWorkers(): void {
    this.loadingWorkers = true;
    this.http.get<Worker[]>(`${this.api}/api/users/workers`).subscribe({
      next: (workers) => {
        this.workers        = workers;
        this.loadingWorkers = false;
      },
      error: (err) => {
        console.error('[CreateJob] Failed to load workers:', err);
        this.loadingWorkers = false;
      }
    });
  }


  // ─────────────────────────────────────────────────────
  // MANUAL COORDINATE ENTRY
  // Allows the manager to type coordinates directly for
  // remote locations (oil rigs, bush sites, offshore).
  // ─────────────────────────────────────────────────────

  enterCoordinatesManually(): void {
    Swal.fire({
      title:  'Enter Coordinates Manually',
      html:
        `<p style="font-size:13px;color:#6B7280;margin-bottom:16px">
          Use this for remote locations (offshore, bush sites) that do not
          appear in Google Maps search results.
        </p>
        <div style="display:flex;flex-direction:column;gap:10px;text-align:left">
          <div>
            <label style="font-size:12px;font-weight:600;color:#374151">
              Location Display Name
            </label>
            <input id="swal-name" class="swal2-input"
                   placeholder="e.g. OML 118 — Bonga Field"
                   style="margin:4px 0 0;font-size:13px"
                   value="${this.locationName}">
          </div>
          <div>
            <label style="font-size:12px;font-weight:600;color:#374151">
              Latitude
            </label>
            <input id="swal-lat" class="swal2-input" type="number"
                   step="any" placeholder="e.g. 3.395987"
                   style="margin:4px 0 0;font-size:13px"
                   value="${this.latitude ?? ''}">
          </div>
          <div>
            <label style="font-size:12px;font-weight:600;color:#374151">
              Longitude
            </label>
            <input id="swal-lng" class="swal2-input" type="number"
                   step="any" placeholder="e.g. 7.085628"
                   style="margin:4px 0 0;font-size:13px"
                   value="${this.longitude ?? ''}">
          </div>
        </div>`,
      showCancelButton:  true,
      confirmButtonText: 'Set Location',
      confirmButtonColor: '#2BB0A6',
      cancelButtonColor:  '#6B7280',
      preConfirm: () => {
        const name = (document.getElementById('swal-name') as HTMLInputElement).value.trim();
        const lat  = parseFloat((document.getElementById('swal-lat') as HTMLInputElement).value);
        const lng  = parseFloat((document.getElementById('swal-lng') as HTMLInputElement).value);

        if (!name)
          return Swal.showValidationMessage('Please enter a location name.');
        if (isNaN(lat) || lat < -90  || lat > 90)
          return Swal.showValidationMessage('Please enter a valid latitude (-90 to 90).');
        if (isNaN(lng) || lng < -180 || lng > 180)
          return Swal.showValidationMessage('Please enter a valid longitude (-180 to 180).');

        return { name, lat, lng };
      }
    }).then(result => {
      if (!result.isConfirmed || !result.value) return;

      this.locationName      = result.value.name;
      this.latitude          = result.value.lat;
      this.longitude         = result.value.lng;
      this.locationConfirmed = true;

      // Update the input field to show the chosen name.
      if (this.locationInputRef?.nativeElement)
        this.locationInputRef.nativeElement.value = this.locationName;
    });
  }


  // ─────────────────────────────────────────────────────
  // FORM VALIDATION
  // ─────────────────────────────────────────────────────

  get isFormValid(): boolean {
    return !!(
      this.title.trim()        &&
      this.description.trim()  &&
      this.locationName.trim() &&
      this.assignedDate
    );
  }


  // ─────────────────────────────────────────────────────
  // SUBMIT — CREATE JOB
  // ─────────────────────────────────────────────────────

  createJob(): void {
    if (!this.isFormValid) return;
    if (this.loading) return;

    this.loading = true;
    this.error   = null;

    const payload = {
      title:          this.title.trim(),
      description:    this.description.trim(),
      locationName:   this.locationName.trim(),
      assignedDate:   new Date(this.assignedDate).toISOString(),
      assignedUserId: this.assignedUserId || null,

      // ✅ Coordinates — null when location was not confirmed via Places.
      // The field worker can still see the job; Navigate will be hidden.
      latitude:       this.latitude,
      longitude:      this.longitude
    };

    this.http.post(`${this.api}/api/jobs`, payload).subscribe({
      next: () => {
        this.loading = false;
        this.success = true;

        const coordMsg = this.locationConfirmed
          ? `Coordinates captured: ${this.latitude?.toFixed(4)}, ${this.longitude?.toFixed(4)}`
          : 'No GPS coordinates — workers will not see a map for this job.';

        Swal.fire({
          title:             'Job Created',
          html:              `<p>Job "<strong>${this.title}</strong>" has been created.</p>
                              <p style="font-size:12px;color:#6B7280;margin-top:8px">
                                ${coordMsg}
                              </p>`,
          icon:              'success',
          confirmButtonColor: '#2BB0A6'
        }).then(() => {
          this.router.navigate(['/jobs']);
        });
      },
      error: (err) => {
        this.loading = false;
        this.error   = err?.error?.Message
                    ?? err?.error?.message
                    ?? 'Failed to create job. Please try again.';
      }
    });
  }


  // ─────────────────────────────────────────────────────
  // CLEAR LOCATION
  // ─────────────────────────────────────────────────────

  clearLocation(): void {
    this.locationName      = '';
    this.latitude          = null;
    this.longitude         = null;
    this.locationConfirmed = false;

    if (this.locationInputRef?.nativeElement)
      this.locationInputRef.nativeElement.value = '';
  }
}